import React from 'react';
import { i18n } from 'config/locales';
import DebugRender from 'components/Debug/Render/DebugRender';
import PropTypes from 'prop-types';
import ValidationElement from '../ValidationElement';
import Field from '../Field';
import Show from '../Show';
import Text from '../Text';
import Checkbox from '../Checkbox';
import SelectDropdown from '../SelectDropdown';
import { setAttribute } from '../shared/setAttribute';

const propTypes = {
  first: PropTypes.string,
  firstInitialOnly: PropTypes.bool,
  last: PropTypes.string,
  middle: PropTypes.string,
  middleInitialOnly: PropTypes.bool,
  noFirstName: PropTypes.bool,
  hideNoFirstName: PropTypes.bool,
  noMiddleName: PropTypes.bool,
  hideMiddleName: PropTypes.bool,
  suffix: PropTypes.string,
  suffixOther: PropTypes.string,
  prefix: PropTypes.string,
  focus: PropTypes.bool,
  error: PropTypes.bool,
  valid: PropTypes.bool,
  errorCodes: PropTypes.array,
  onUpdate: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  title: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.string,
};

export default class Name extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      first: this.props.first,
      middle: this.props.middle,
    };

    this.first = React.createRef();
    this.firstInitialOnly = React.createRef();
    this.middle = React.createRef();
    this.noFirstName = React.createRef();
    this.noMiddleName = React.createRef();
    this.middleInitialOnly = React.createRef();
    this.suffixRef = React.createRef();
  }

  // Removes non-letter characters on blur if somehow added, such as by copy/paste
  lettersOnly = (value = '') => {
    let result = value;
    if (!(/^(\s*|[a-zA-Z]+)$/.exec(value))) {
      result = value.replace(/[^a-zA-Z]/g, '');
    }
    return result;
  };

  lettersOnlyOnKeyPress = (e) => {
    const code = e.keyCode ? e.keyCode : e.which; // permit letters and navigation
    const backspace = 8;
    const tabKey = 9;
    const deleteKey = 46;
    if (!((code >= 65 && code <= 90) // Letters
      || (code >= 35 && code <= 40) // Arrow Keys Home and End
      || (code === backspace)
      || (code === tabKey)
      || (code === deleteKey)
    )) {
      e.preventDefault();
    }
  };

  update = (queue, callback) => {
    this.setState(
      {
        first: this.props.first.trim(),
        firstInitialOnly: this.props.firstInitialOnly,
        last: this.props.last.trim(),
        middle: this.props.middle.trim(),
        middleInitialOnly: this.props.middleInitialOnly,
        hideMiddleName: this.props.hideMiddleName,
        noFirstName: this.props.noFirstName,
        hideNoFirstName: this.props.hideNoFirstName,
        noMiddleName: this.props.noMiddleName,
        suffix: this.props.suffix,
        suffixOther: this.props.suffixOther.trim(),
      },
      () => {
        this.props.onUpdate({
          first: this.props.first.trim(),
          firstInitialOnly: this.props.firstInitialOnly,
          last: this.props.last.trim(),
          middle: this.props.middle.trim(),
          middleInitialOnly: this.props.middleInitialOnly,
          hideMiddleName: this.props.hideMiddleName,
          noFirstName: this.props.noFirstName,
          hideNoFirstName: this.props.hideNoFirstName,
          noMiddleName: this.props.noMiddleName,
          suffix: this.props.suffix,
          suffixOther: this.props.suffixOther.trim(),
          ...queue,
        });
      }
    );

    if (callback) {
      callback();
    }
  };

  updateFirstInitial = (values) => {
    this.update({
      noFirstName: false,
      firstInitialOnly: values.checked,
    });
  };

  updateMiddleInitial = (values) => {
    this.update({
      noMiddleName: false,
      middleInitialOnly: values.checked,
    });
  };

  updateFirstNone = (values) => {
    let first = '';
    if (!values.checked) { first = this.state.first; }
    this.update({
      firstInitialOnly: false,
      first,
      noFirstName: values.checked,
    });
  };

  updateMiddleNone = (values) => {
    let middle = '';
    if (!values.checked) { middle = this.state.middle; }
    this.update({
      middleInitialOnly: false,
      middle,
      noMiddleName: values.checked,
    });
  };

  handleErrorFirst = (value, arr) => this.handleError('first', value, arr);

  handleErrorMiddle = (value, arr) => this.handleError('middle', value, arr);

  handleErrorLast = (value, arr) => this.handleError('last', value, arr);

  handleErrorSuffix = (value, arr) => this.handleError('suffix', value, arr);

  handleError = (code, value, arr) => {
    let errors = arr.map((err) => ({
      code: `name.${code}.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    errors = errors.concat(
      this.constructor.errors.map((err) => ({
        code: `name.${err.code}`,
        valid: err.func(value, { ...this.props, ...this.state }),
        uid: this.state.uid,
      }))
    );

    // Take the original and concatenate our new error values to it
    return this.props.onError(value, errors);
  };

  getSuffixOptions = () => {
    const { prefix } = this.props;
    return [
      { label: '', value: '' },
      { label: i18n.t(`${prefix}.label.jr`), value: 'Jr' },
      { label: i18n.t(`${prefix}.label.sr`), value: 'Sr' },
      { label: i18n.t(`${prefix}.label.i`), value: 'I' },
      { label: i18n.t(`${prefix}.label.ii`), value: 'II' },
      { label: i18n.t(`${prefix}.label.iii`), value: 'III' },
      { label: i18n.t(`${prefix}.label.iv`), value: 'IV' },
      { label: i18n.t(`${prefix}.label.v`), value: 'V' },
      { label: i18n.t(`${prefix}.label.vi`), value: 'VI' },
      { label: i18n.t(`${prefix}.label.vii`), value: 'VII' },
      { label: i18n.t(`${prefix}.label.viii`), value: 'VIII' },
      { label: i18n.t(`${prefix}.label.ix`), value: 'IX' },
      { label: i18n.t(`${prefix}.label.x`), value: 'X' },
      { label: i18n.t(`${prefix}.label.other`), value: 'Other' },
    ];
  };

  filterErrors = (errors) => errors.filter((err) => err.code.indexOf('required') === -1);

  render() {
    const { prefix, textPrefix } = this.props;
    const klass = [
      `name ${this.props.className || ''}`.trim(),
      this.props.disabled ? 'disabled' : '',
    ];
    const maxFirst = this.props.firstInitialOnly ? '1' : '100';
    const maxMiddle = this.props.middleInitialOnly ? '1' : '100';
    const maxLast = '100';

    return (
      <div className={klass.join(' ')}>
        <DebugRender parent={this} />
        {this.props.title && <h2>{this.props.title}</h2>}
        <Field
          title={i18n.t(`${prefix}.label.first`)}
          titleSize="label"
          help={`identification.${textPrefix}.first.help`}
          errorPrefix="name"
          className="usa-form-control"
          filterErrors={this.filterErrors}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="labels"
          attribute={setAttribute(this.props.attribute, 'first')}
        >
          <Text
            name="first"
            ariaLabel="First Name"
            ref={this.first}
            pattern="^[a-zA-Z\-\.' ]*$"
            prefilter={this.props.firstInitialOnly ? this.lettersOnly : undefined}
            onKeyDown={this.props.firstInitialOnly ? this.lettersOnlyOnKeyPress : undefined}
            minlength={this.props.firstInitialOnly ? 1 : 2}
            maxlength={maxFirst}
            additionalLengthErrors
            className="first"
            value={this.props.first}
            attribute={setAttribute(this.props.attribute, 'first')}
            onUpdate={(values) => {
              this.update({
                first: values.value.trim(),
              });
            }}
            onError={this.handleErrorFirst}
            onFocus={this.props.onFocus}
            required={!this.props.noFirstName && this.props.required}
            disabled={(this.props.noFirstName && this.props.first?.length === 0) || this.props.disabled}
          />
          <div className="flags">
            {!this.props.hideNoFirstName && (
              <Checkbox
                name="noFirstName"
                ariaLabel="No First Name"
                ref={this.noFirstName}
                label={i18n.t(`${prefix}.label.noFirst`)}
                className="first-none"
                toggle="false"
                value={this.props.noFirstName}
                checked={this.props.noFirstName}
                onUpdate={this.updateFirstNone}
                onError={this.handleErrorFirst}
                disabled={this.props.disabled}
              />
            )}
            <Checkbox
              name="firstInitialOnly"
              ariaLabel="First Initial Only"
              ref={this.firstInitialOnly}
              label={i18n.t(`${prefix}.label.initialOnly`)}
              className="first-initial-only"
              toggle="false"
              value={this.props.firstInitialOnly}
              checked={this.props.firstInitialOnly}
              onUpdate={this.updateFirstInitial}
              onError={this.handleErrorFirst}
              disabled={this.props.disabled}
            />
          </div>
        </Field>
        {!this.props.hideMiddleName && (
          <Field
            title={i18n.t(`${prefix}.label.middle`)}
            titleSize="label"
            help={`identification.${textPrefix}.middle.help`}
            errorPrefix="name"
            className="usa-form-control"
            filterErrors={this.filterErrors}
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="labels"
            attribute={setAttribute(this.props.attribute, 'middle')}
          >
            <Text
              name="middle"
              ariaLabel="Middle Name"
              ref={this.middle}
              pattern="^[a-zA-Z\-\.' ]*$"
              prefilter={this.props.middleInitialOnly ? this.lettersOnly : undefined}
              onKeyDown={this.props.middleInitialOnly ? this.lettersOnlyOnKeyPress : undefined}
              minlength={this.props.middleInitialOnly ? 1 : 2}
              maxlength={maxMiddle}
              additionalLengthErrors
              className="middle"
              value={this.props.middle}
              disabled={
                (this.props.noMiddleName && this.props.middle?.length === 0) || this.props.disabled
              }
              onUpdate={(values) => {
                this.update({
                  middle: values.value.trim(),
                });
              }}
              onError={this.handleErrorMiddle}
              onFocus={this.props.onFocus}
              required={!this.props.noMiddleName && this.props.required}
              attribute={setAttribute(this.props.attribute, 'middle')}
            />
            <div className="middle-options flags">
              <Checkbox
                name="noMiddleName"
                ariaLabel="No Middle Name"
                ref={this.noMiddleName}
                label={i18n.t(`${prefix}.label.noMiddle`)}
                className="middle-none"
                toggle="false"
                value={this.props.noMiddleName}
                checked={this.props.noMiddleName}
                onUpdate={this.updateMiddleNone}
                onError={this.handleErrorMiddle}
                disabled={this.props.disabled}
              />
              <Checkbox
                name="middleInitialOnly"
                ariaLabel="Middle Initial Only"
                ref={this.middleInitialOnly}
                label={i18n.t(`${prefix}.label.initialOnly`)}
                className="middle-initial-only"
                toggle="false"
                value={this.props.middleInitialOnly}
                checked={this.props.middleInitialOnly}
                onUpdate={this.updateMiddleInitial}
                onError={this.handleErrorMiddle}
                disabled={this.props.disabled}
              />
            </div>
          </Field>
        )}
        <Field
          title={i18n.t(`${prefix}.label.last`)}
          titleSize="label"
          errorPrefix="name"
          className="usa-form-control"
          filterErrors={this.filterErrors}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="labels"
          attribute={setAttribute(this.props.attribute, 'last')}
        >
          <Text
            name="last"
            ariaLabel="Last Name"
            minlength={1}
            maxlength={maxLast}
            className="last"
            pattern="^[a-zA-Z\-\.' ]*$"
            value={this.props.last}
            onUpdate={(values) => {
              this.update({
                last: values.value.trim(),
              });
            }}
            onError={this.handleErrorLast}
            onFocus={this.props.onFocus}
            required={this.props.required}
            disabled={this.props.disabled}
            attribute={setAttribute(this.props.attribute, 'last')}
          />
        </Field>
        {/*
         * Hacky spacer because <Field /> padding doens't stack properly.
         * This avoids having to alter the <Field /> and regressing every usage.
         * Permanent fix should be done in <Field /> and remove this hack.
         */}
        <div style={{ height: '50px' }} />
        <Field
          title={i18n.t(`${prefix}.label.suffix`)}
          titleSize="label"
          help={`identification.${textPrefix}.suffix.help`}
          errorPrefix="name"
          className="usa-form-control"
          filterErrors={this.filterErrors}
          scrollIntoView={this.props.scrollIntoView}
          optional
          optionalText={i18n.t(`${prefix}.label.optional`)}
          labelFor={`selectDropdown-${this.state.uid}`}
          attribute={setAttribute(this.props.attribute, 'suffix')}
        >
          <SelectDropdown
            className="option-list suffix usa-small-input"
            isDisabled={this.props.disabled}
            ref={this.suffixRef}
            name="suffix"
            ariaLabel="Name Suffix"
            onChange={(e) => {
              this.update({
                suffix: e.target.value,
              });
            }}
            onError={this.handleErrorSuffix}
            value={this.props.suffix || ''}
            required={false}
            labelKey={`selectDropdown-${this.state.uid}`}
            attribute={setAttribute(this.props.attribute, 'suffix')}
            id={`selectDropdown-${this.state.uid}`}
          >
            {this.getSuffixOptions().map((option) => (
              <option key={`name-${option.label}`} label={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectDropdown>
          <Show when={this.props.suffix === 'Other'}>
            <Text
              attribute={setAttribute(this.props.attribute, 'suffixOther')}
              name="suffixOther"
              ariaLabel="Other Suffix"
              label={i18n.t(`${prefix}.label.other`)}
              maxlength="100"
              className="suffix-other"
              value={this.props.suffixOther}
              onUpdate={(values) => {
                this.update({
                  suffixOther: values.value.trim(),
                });
              }}
              onError={this.handleErrorSuffix}
              onFocus={this.props.onFocus}
              required={this.props.required && this.props.suffix === 'Other'}
              disabled={this.props.disabled}
            />
          </Show>
        </Field>
      </div>
    );
  }
}

Name.propTypes = propTypes;

Name.defaultProps = {
  first: '',
  firstInitialOnly: false,
  last: '',
  middle: '',
  middleInitialOnly: false,
  noFirstName: false,
  hideNoFirstName: false,
  noMiddleName: false,
  hideMiddleName: false,
  suffix: '',
  suffixOther: '',
  prefix: 'name',
  textPrefix: 'name',
  focus: false,
  error: false,
  valid: false,
  required: false,
  errorCodes: [],
  onUpdate: () => { },
  onError: (value, arr) => arr,
};

Name.requiredErrorsOnly = (errors) => errors.filter((err) => err.code.indexOf('required') !== -1);

Name.errors = [];
